import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import type { BidDetail as BidDetailModel } from '@bidmanager/shared';
import { errorMessage, proxiedUrl, request } from '../api';

/**
 * The audit view (§2.2.3).
 *
 * This page is the product's reason for existing: the client can see what job
 * was applied for, read the posting the documents were written against, and
 * download the exact PDFs that went out under their name — plus the screenshots
 * proving the form was actually submitted.
 *
 * The document links are short-lived presigned URLs minted per request, so this
 * page must be re-fetched rather than bookmarked. That is intentional: a
 * durable link would be a permanent unauthenticated route to someone's resume.
 */
export function BidDetail(): JSX.Element {
  const { bidId } = useParams<{ bidId: string }>();

  const { data, error, isLoading } = useQuery({
    queryKey: ['bid', bidId],
    queryFn: () => request<BidDetailModel>('GET', `/bids/${bidId}`),
    enabled: Boolean(bidId),
    // The presigned URLs in this payload expire; refetching on mount keeps the
    // download buttons from silently 403ing on a stale tab.
    staleTime: 60_000,
  });

  if (isLoading) return <p className="muted">Loading…</p>;
  if (error) return <p className="error">{errorMessage(error)}</p>;
  if (!data) return <p className="muted">Not found.</p>;

  return (
    <>
      <header className="page-head">
        <Link to="/bids" className="small">
          ← All bids
        </Link>
        <h1 style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
          {data.companyName} — {data.role}
          {data.jdMatchPercent !== null && (
            <span
              className={`badge ${data.jdMatchPercent >= 60 ? 'ok' : 'warn'}`}
              style={{ fontSize: 13 }}
              title="How much of the posting these documents address — a coverage check, not an ATS score."
            >
              {data.jdMatchPercent}% match
            </span>
          )}
        </h1>
        <p className="muted small" style={{ margin: 0 }}>
          {/* Same zone the list groups and displays in — two views of one bid
              showing two different times is worse than either choice alone. */}
          Submitted by {data.bidderName} for {data.profileName} on{' '}
          {new Date(data.placedAt).toLocaleString([], { timeZone: data.timezone })} (
          {data.timezone})
          {data.desktopVersion && ` · app ${data.desktopVersion}`}
        </p>
      </header>

      <div className="stack">
        <section className="card">
          <div className="row" style={{ marginBottom: 10 }}>
            <strong>Documents submitted</strong>
            <span className="muted small">Links expire shortly</span>
          </div>
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            {/*
              Opened in a new tab rather than fetched and re-served — the
              presigned URL is still the capability. Routed through this
              app's own origin (proxiedUrl) rather than linked directly at
              the API's origin, so it goes through the same /api/* proxy as
              everything else instead of hitting the API host straight from
              the browser — see proxiedUrl's doc comment in ../api.ts.
            */}
            <a href={proxiedUrl(data.resumePdfUrl)} target="_blank" rel="noreferrer">
              <button>Resume (PDF)</button>
            </a>
            <a href={proxiedUrl(data.coverLetterPdfUrl)} target="_blank" rel="noreferrer">
              <button>Cover letter (PDF)</button>
            </a>
            <a href={data.finalUrl} target="_blank" rel="noreferrer">
              <button className="ghost">Job posting ↗</button>
            </a>
          </div>
        </section>

        <section className="card">
          <div className="row" style={{ marginBottom: 10 }}>
            <strong>Screenshots</strong>
            <span className="muted small">{data.screenshots.length} attached</span>
          </div>
          {data.screenshots.length === 0 ? (
            <p className="warn small" style={{ margin: 0 }}>
              No screenshots were attached to this bid.
            </p>
          ) : (
            <div className="shots">
              {data.screenshots.map((shot) => (
                <a key={shot.id} href={proxiedUrl(shot.url)} target="_blank" rel="noreferrer">
                  <img
                    src={proxiedUrl(shot.url)}
                    alt={`Screenshot captured ${new Date(shot.capturedAt).toLocaleString()}`}
                    title={`${shot.width}×${shot.height} · ${new Date(
                      shot.capturedAt,
                    ).toLocaleString()}`}
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          )}
        </section>

        <section className="card">
          <div className="row" style={{ marginBottom: 10 }}>
            <strong>Job description</strong>
            <span className="muted small">Stored as it was at submission</span>
          </div>
          {/*
            Kept verbatim because job postings are routinely taken down, and
            the server verified this text hashes to the one the documents were
            generated against — so it is provably the posting they answer.
          */}
          <div className="jd">{data.jobDescription}</div>
        </section>
      </div>
    </>
  );
}
